import React, { useEffect, useState, useRef } from 'react';
import {
  Activity,
  ExternalLink,
  ShieldAlert,
  Bot,
  Settings2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  ChevronRight,
  Filter,
  FileText,
  Inbox as InboxIcon,
  ChevronDown,
  Send,
  Archive,
  Zap,
  RefreshCw,
  UserCheck,
  UserCog,
  Search,
} from 'lucide-react';
import AppShell from '../components/AppShell';
import axiosInstance from '../utils/axiosInstance';

// ---------------------------------------------------------------------------
// shared
// ---------------------------------------------------------------------------

const STATUS_STYLES = {
  ready: { icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  partial: { icon: AlertTriangle, cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  missing: { icon: XCircle, cls: 'bg-stone-100 text-stone-700 border-stone-200' },
  human: { icon: ShieldAlert, cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  error: { icon: XCircle, cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  pending: { icon: Clock, cls: 'bg-stone-100 text-stone-600 border-stone-200' },
};

const StatusBadge = ({ status }) => {
  const def = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = def.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide ${def.cls}`}>
      <Icon className="h-3 w-3" />
      {status}
    </span>
  );
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
};

// ---------------------------------------------------------------------------
// LIVE VIEW - operational dashboard
// ---------------------------------------------------------------------------

const STAT_TONES = {
  slate:   { border: 'border-slate-200',   bg: 'bg-white',      text: 'text-slate-900',   accent: 'text-slate-500' },
  indigo:  { border: 'border-indigo-200',  bg: 'bg-indigo-50',  text: 'text-indigo-900',  accent: 'text-indigo-600' },
  emerald: { border: 'border-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-900', accent: 'text-emerald-600' },
  amber:   { border: 'border-amber-200',   bg: 'bg-amber-50',   text: 'text-amber-900',   accent: 'text-amber-700' },
  rose:    { border: 'border-rose-200',    bg: 'bg-rose-50',    text: 'text-rose-900',    accent: 'text-rose-600' },
};

const BigStat = ({ label, value, tone = 'slate', icon: Icon, sub }) => {
  const t = STAT_TONES[tone];
  return (
    <div className={`rounded-xl border ${t.border} ${t.bg} px-4 py-3`}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon className={`h-3 w-3 ${t.accent}`} />}
        <span className={`text-[0.6rem] font-semibold uppercase tracking-[0.1em] ${t.accent}`}>
          {label}
        </span>
      </div>
      <div className={`font-display text-3xl font-semibold ${t.text} leading-none`}>
        {value ?? 0}
      </div>
      {sub && <div className={`text-[0.65rem] ${t.accent} mt-1 truncate`}>{sub}</div>}
    </div>
  );
};

// Inline pill used for the auto-actions strip. Single-line, high-density.
// Renders as a button when onClick is provided, so drill-down on a chip
// is discoverable (hover ring + cursor change).
const ChipStat = ({ label, value, tone = 'slate', icon: Icon, onClick, title }) => {
  const t = STAT_TONES[tone];
  const classes = `inline-flex items-center gap-2 rounded-lg border ${t.border} ${t.bg} px-3 py-1.5 ${
    onClick ? 'cursor-pointer hover:ring-2 hover:ring-[hsl(var(--ring))] transition' : ''
  }`;
  const inner = (
    <>
      {Icon && <Icon className={`h-3 w-3 ${t.accent}`} />}
      <span className={`text-[0.65rem] font-medium uppercase tracking-wider ${t.accent}`}>
        {label}
      </span>
      <span className={`text-sm font-semibold font-mono ${t.text}`}>{value ?? 0}</span>
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} title={title} className={classes}>
        {inner}
      </button>
    );
  }
  return <span className={classes} title={title}>{inner}</span>;
};

const FunnelNode = ({ label, value, icon: Icon, tone = 'slate', sub }) => {
  const tones = {
    slate:   'bg-white border-slate-200 text-slate-900',
    indigo:  'bg-indigo-50 border-indigo-200 text-indigo-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
    rose:    'bg-rose-50 border-rose-200 text-rose-900',
  };
  return (
    <div
      className={`basis-0 flex-grow shrink-0 min-w-[100px] h-[68px] rounded-lg border ${tones[tone]} px-3 py-2 flex flex-col justify-between`}
    >
      <div className="flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3 opacity-70 shrink-0" />}
        <span className="text-[0.55rem] font-semibold uppercase tracking-wider opacity-70 truncate">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <div className="font-display text-xl font-semibold leading-none">{value ?? 0}</div>
        {sub && <div className="text-[0.6rem] opacity-60 truncate">{sub}</div>}
      </div>
    </div>
  );
};

const FunnelArrow = () => (
  <div className="flex items-center justify-center shrink-0 text-stone-300 w-4">
    <ChevronRight className="h-3.5 w-3.5" />
  </div>
);

const DecisionRow = ({ r, isNew, onOpen }) => {
  const cat = r.category || '-';
  const src = r.selection_source;
  return (
    <button
      type="button"
      onClick={() => onOpen(r.run_id)}
      className={`w-full text-left flex items-start gap-3 py-2 px-3 rounded-lg border transition-colors cursor-pointer ${
        isNew ? 'border-emerald-200 bg-emerald-50/50' : 'border-transparent hover:bg-stone-50'
      }`}
    >
      <StatusBadge status={r.status} />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-foreground truncate">
          {r.subject || '(no subject)'}
        </div>
        <div className="text-[0.65rem] text-muted-foreground flex items-center gap-2 flex-wrap">
          <span className="font-mono">{cat}</span>
          {r.triage_ran && (
            <span className={r.triage_escalated ? 'text-indigo-700' : 'text-sky-700'}>
              {r.triage_escalated ? '→ human' : 'triaged'}
            </span>
          )}
          {src === 'llm' && <span className="text-emerald-700">LLM · {r.tool_count} tools</span>}
          {src === 'rule' && <span>rule-based</span>}
          {src === 'none' && <span>no endpoints</span>}
          {r.note_posted_by === 'auto' && (
            <span className="text-emerald-700 font-medium">🤖 auto-posted</span>
          )}
          {r.note_posted_by === 'human' && (
            <span className="text-slate-700">posted by human</span>
          )}
          {!r.note_posted_by && r.auto_post && r.auto_post.feature_on && r.auto_post.eligible === false && (
            <span className="text-amber-700" title={r.auto_post.reason}>
              held back
            </span>
          )}
          <span className="text-muted-foreground/60">· {timeAgo(r.created_at)}</span>
        </div>
      </div>
      <a
        href={`https://lamptwist.gorgias.com/app/ticket/${r.gorgias_id}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="shrink-0 text-muted-foreground hover:text-foreground"
      >
        <ExternalLink className="h-3 w-3" />
      </a>
    </button>
  );
};

// Full-detail modal for a DispatchRun. Shows every stage of the agent's
// thinking: ticket input, classifier, triage, tool selections, endpoint
// calls with args + data preview, draft, auto-post decision. The point
// is to give the operator enough context to decide HOW to fine-tune
// (prompt, threshold, rules) when a draft falls short.
const RunDetailModal = ({ runId, onClose }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!runId) return;
    setLoading(true);
    setData(null);
    setError('');
    axiosInstance
      .get(`api/replai/dispatch-runs/${runId}/detail/`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.error || e.message || 'failed'))
      .finally(() => setLoading(false));
  }, [runId]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!runId) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {loading && (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        )}
        {error && (
          <div className="p-10 text-center text-sm text-rose-700">{error}</div>
        )}
        {data && (
          <>
            <header className="px-6 py-4 border-b border-border/70 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-lg font-semibold text-foreground truncate">
                    {data.subject || '(no subject)'}
                  </h3>
                  <StatusBadge status={data.status} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  #{data.gorgias_id} · {data.customer_email || 'unknown'} · {data.language || '-'} · {new Date(data.created_at).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={`https://lamptwist.gorgias.com/app/ticket/${data.gorgias_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-muted-foreground hover:bg-[hsl(var(--secondary))] hover:text-foreground"
                >
                  <ExternalLink className="h-3 w-3" />
                  Gorgias
                </a>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-1.5 text-muted-foreground hover:bg-[hsl(var(--secondary))] hover:text-foreground"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              </div>
            </header>

            <div className="overflow-y-auto p-6 space-y-4 text-xs">
              {/* Customer message */}
              {data.first_body && (
                <Section label="Customer message" tone="stone">
                  <pre className="whitespace-pre-wrap font-sans text-foreground/90 leading-relaxed">
                    {data.first_body}
                  </pre>
                </Section>
              )}

              {/* Classifier */}
              <Section label="1 · Classifier" tone="stone">
                <KV k="category" v={data.classifier?.category || data.category} />
                <KV
                  k="confidence"
                  v={
                    typeof data.classifier?.confidence === 'number'
                      ? `${(data.classifier.confidence * 100).toFixed(0)}%`
                      : '-'
                  }
                />
                {data.classifier?.reason && <KV k="reason" v={data.classifier.reason} />}
                {Array.isArray(data.classifier?.rule_fires) && data.classifier.rule_fires.length > 0 && (
                  <div className="mt-2">
                    <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1">rule fires</div>
                    <ul className="space-y-0.5">
                      {data.classifier.rule_fires.map((rf, i) => (
                        <li key={i} className="font-mono text-[0.7rem] text-foreground/80">
                          {rf.category} · score {rf.score}{rf.required?.length ? ` · matched ${rf.required.join(', ')}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Section>

              {/* Triage */}
              <Section
                label="2 · Triage"
                tone={data.triage?.escalated ? 'indigo' : data.triage?.ran ? 'sky' : 'stone'}
              >
                {!data.triage?.ran && (
                  <p className="text-muted-foreground">
                    Skipped - classifier was confident enough.
                  </p>
                )}
                {data.triage?.ran && (
                  <>
                    <KV k="category" v={data.triage.category || '-'} />
                    <KV
                      k="AI handles"
                      v={data.triage.ai_should_handle ? 'yes' : 'no - escalated to human'}
                    />
                    {data.triage.reason && <KV k="reason" v={data.triage.reason} />}
                  </>
                )}
              </Section>

              {/* Selection */}
              <Section
                label="3 · Tool selection"
                tone={data.selection_source === 'llm' ? 'emerald' : 'stone'}
              >
                <KV
                  k="source"
                  v={
                    data.selection_source === 'llm'
                      ? 'LLM (tool_use)'
                      : data.selection_source === 'rule'
                        ? 'rule-based (by_category)'
                        : data.selection_source === 'none'
                          ? 'skipped (triage escalated)'
                          : '-'
                  }
                />
                {(data.agentic_selections || []).length > 0 && (
                  <div className="mt-2">
                    <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1">
                      LLM picked
                    </div>
                    <ul className="space-y-1">
                      {data.agentic_selections.map((s, i) => (
                        <li key={i} className="font-mono text-[0.7rem] text-foreground/80">
                          {s.tool_name} ({Object.entries(s.args || {}).map(([k, v]) => `${k}=${v}`).join(', ') || 'no args'})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Section>

              {/* Endpoint calls */}
              <Section label={`4 · Endpoint calls (${(data.endpoint_calls || []).length})`} tone="stone">
                {(data.endpoint_calls || []).length === 0 && (
                  <p className="text-muted-foreground">No endpoints were called.</p>
                )}
                <ul className="space-y-2">
                  {(data.endpoint_calls || []).map((c, i) => (
                    <li key={i} className="rounded-lg border border-border p-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-mono text-[0.7rem] text-foreground">
                          {c.tool_name || c.endpoint}
                        </span>
                        <span
                          className={`font-mono text-[0.65rem] shrink-0 ${
                            c.status === 'implemented' ? 'text-emerald-700'
                            : c.status === 'stub' ? 'text-sky-700'
                            : c.status === 'error' ? 'text-rose-700'
                            : 'text-muted-foreground'
                          }`}
                        >
                          {c.status}
                        </span>
                      </div>
                      {c.args && Object.keys(c.args).length > 0 && (
                        <div className="font-mono text-[0.65rem] text-muted-foreground mt-0.5">
                          args: {Object.entries(c.args).map(([k, v]) => `${k}=${v}`).join(', ')}
                        </div>
                      )}
                      {c.error && (
                        <div className="text-[0.65rem] text-rose-700 mt-1">{c.error}</div>
                      )}
                      {c.data_preview && (
                        <pre className="mt-1 text-[0.65rem] font-mono text-foreground/70 bg-stone-50 p-1.5 rounded max-h-32 overflow-y-auto whitespace-pre-wrap">
                          {c.data_preview}
                        </pre>
                      )}
                    </li>
                  ))}
                </ul>
              </Section>

              {/* Draft */}
              <Section
                label={`5 · Draft${data.draft_audience === 'agent' ? ' (for agent, internal)' : ''}`}
                tone={
                  data.status === 'ready' ? 'emerald'
                  : data.status === 'human' ? 'indigo'
                  : 'stone'
                }
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-[0.7rem]">
                    confidence {typeof data.draft_confidence === 'number' ? `${Math.round(data.draft_confidence * 100)}%` : '-'}
                  </span>
                  {data.draft_confidence_reason && (
                    <span className="text-[0.65rem] text-muted-foreground italic truncate">
                      "{data.draft_confidence_reason}"
                    </span>
                  )}
                </div>
                <pre className="whitespace-pre-wrap font-sans text-foreground/90 leading-relaxed bg-stone-50 p-3 rounded-lg">
                  {data.draft_reply || <span className="italic text-muted-foreground">(empty)</span>}
                </pre>
                {data.missing_reason && (
                  <p className="text-[0.65rem] text-amber-800 mt-2">
                    <span className="font-medium">Outcome:</span> {data.missing_reason}
                  </p>
                )}
              </Section>

              {/* Auto-post */}
              {data.auto_post && (data.auto_post.posted || data.auto_post.reason || data.auto_post.error) && (
                <Section
                  label="6 · Auto-post decision"
                  tone={data.auto_post.posted ? 'emerald' : data.auto_post.error ? 'rose' : 'amber'}
                >
                  {data.auto_post.posted ? (
                    <p className="text-emerald-900">
                      ✓ Pushed to Gorgias as internal note
                      {typeof data.auto_post.threshold === 'number' &&
                        ` (confidence ≥ ${Math.round(data.auto_post.threshold * 100)}%)`}
                      {data.note_posted_by === 'auto' ? ' · automatically' : ''}.
                    </p>
                  ) : data.auto_post.error ? (
                    <p className="text-rose-900">Gorgias post failed: {data.auto_post.error}</p>
                  ) : (
                    <p className="text-amber-900">
                      Held back - {data.auto_post.reason}
                      {typeof data.auto_post.threshold === 'number'
                        ? ` (threshold ${Math.round(data.auto_post.threshold * 100)}%)`
                        : ''}.
                    </p>
                  )}
                </Section>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Small helpers for the modal.
const Section = ({ label, tone = 'stone', children }) => {
  const tones = {
    stone:   'border-stone-200 bg-stone-50/40',
    sky:     'border-sky-200 bg-sky-50',
    indigo:  'border-indigo-200 bg-indigo-50',
    emerald: 'border-emerald-200 bg-emerald-50',
    amber:   'border-amber-200 bg-amber-50',
    rose:    'border-rose-200 bg-rose-50',
  };
  return (
    <section className={`rounded-xl border ${tones[tone]} p-3`}>
      <div className="text-[0.6rem] uppercase tracking-[0.1em] font-semibold text-foreground/60 mb-2">
        {label}
      </div>
      {children}
    </section>
  );
};

const KV = ({ k, v }) => (
  <div className="flex gap-2 text-[0.7rem]">
    <span className="text-muted-foreground shrink-0 w-20">{k}</span>
    <span className="text-foreground break-words">{v || <span className="italic text-muted-foreground">-</span>}</span>
  </div>
);

const LiveView = ({ onShowHeldBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastLoad, setLastLoad] = useState(null);
  const prevRunIdsRef = useRef(new Set());
  const [newRunIds, setNewRunIds] = useState(new Set());
  const [openRunId, setOpenRunId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: fresh } = await axiosInstance.get('api/replai/pipeline/live/');
      setData(fresh);
      setLastLoad(new Date());

      // Flash newly-arrived decisions for a few seconds so operators can
      // visually track incoming work without scanning the whole ticker.
      const prev = prevRunIdsRef.current;
      const currentIds = new Set((fresh.recent || []).map((r) => r.run_id));
      const added = new Set();
      for (const id of currentIds) if (!prev.has(id)) added.add(id);
      if (added.size > 0 && prev.size > 0) {
        setNewRunIds(added);
        setTimeout(() => setNewRunIds(new Set()), 4000);
      }
      prevRunIdsRef.current = currentIds;
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const { queue, today, recent } = data;

  return (
    <div className="space-y-4">
      {/* QUEUE STATE - the numbers that matter RIGHT NOW */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Queue state - right now
          </h2>
          <div className="flex items-center gap-1.5 text-[0.6rem] text-muted-foreground">
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            {lastLoad ? `updated ${timeAgo(lastLoad.toISOString())}` : 'loading…'}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <BigStat
            label="Untreated"
            value={queue.untreated}
            icon={InboxIcon}
            tone={queue.untreated > 50 ? 'rose' : queue.untreated > 10 ? 'amber' : 'slate'}
            sub="No dispatch run yet"
          />
          <BigStat
            label="Needs human"
            value={queue.needs_human}
            icon={ShieldAlert}
            tone="indigo"
            sub="AI can't finalise on its own"
          />
          <BigStat
            label="Ready to send"
            value={queue.ready_to_send}
            icon={CheckCircle2}
            tone="emerald"
            sub="Draft ready, not yet posted"
          />
          <BigStat
            label="Posted today"
            value={queue.notes_posted_today}
            icon={Send}
            tone="slate"
            sub={
              typeof today?.auto_posted === 'number' && today.auto_posted > 0
                ? `${today.auto_posted} auto · ${queue.archived_today} archived`
                : `${queue.archived_today} archived today`
            }
          />
        </div>
      </section>

      {/* AUTO-ACTIONS - inline chip strip, keeps vertical density */}
      <section className="flex items-center gap-2 flex-wrap">
        <span className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground mr-2">
          Auto-actions today
        </span>
        <ChipStat
          label="Assigned"
          value={today.auto_assigned}
          icon={UserCheck}
          tone={(today.auto_assigned || 0) > 0 ? 'emerald' : 'slate'}
        />
        <ChipStat
          label="Customer fixed"
          value={today.auto_fixed_customer}
          icon={UserCog}
          tone={(today.auto_fixed_customer || 0) > 0 ? 'emerald' : 'slate'}
        />
        <ChipStat
          label="Posted"
          value={today.auto_posted}
          icon={Bot}
          tone={(today.auto_posted || 0) > 0 ? 'emerald' : 'slate'}
        />
        <ChipStat
          label="Held back"
          value={today.auto_held_back}
          icon={ShieldAlert}
          tone={(today.auto_held_back || 0) > 0 ? 'amber' : 'slate'}
          onClick={onShowHeldBack}
          title="Click to review runs that were held back from auto-posting"
        />
        {typeof today.auto_failed === 'number' && today.auto_failed > 0 && (
          <ChipStat
            label="Post failed"
            value={today.auto_failed}
            icon={XCircle}
            tone="rose"
          />
        )}
        <span className="text-[0.65rem] text-muted-foreground ml-auto">writes back to Gorgias</span>
      </section>

      {/* TODAY'S FUNNEL - the flow of work through the agent */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Today's funnel
          </h2>
          <span className="text-[0.6rem] text-muted-foreground">
            {today.dispatched} dispatches · {today.llm_selected} LLM-picked · {today.triage_escalated} escalated
          </span>
        </div>
        <div className="rounded-xl border border-border bg-white p-2.5 overflow-x-auto">
          <div className="flex items-stretch gap-1 min-w-[800px]">
            <FunnelNode
              label="Ingested"
              value={today.ingested}
              icon={InboxIcon}
            />
            <FunnelArrow />
            <FunnelNode
              label="Dispatched"
              value={today.dispatched}
              icon={Zap}
              sub="runs"
            />
            <FunnelArrow />
            <FunnelNode
              label="Triaged"
              value={today.triaged}
              icon={ShieldAlert}
              tone={today.triage_escalated > 0 ? 'indigo' : 'slate'}
              sub={today.triage_escalated > 0 ? `${today.triage_escalated} → human` : 'LLM verdict'}
            />
            <FunnelArrow />
            <FunnelNode
              label="LLM tools"
              value={today.llm_selected}
              icon={Bot}
              tone={today.llm_selected > 0 ? 'emerald' : 'slate'}
              sub={`${today.rule_selected} rule-based`}
            />
            <FunnelArrow />
            <FunnelNode
              label="Ready draft"
              value={today.by_status?.ready || 0}
              icon={CheckCircle2}
              tone="emerald"
            />
            <FunnelArrow />
            <FunnelNode
              label="Partial"
              value={today.by_status?.partial || 0}
              icon={AlertTriangle}
              tone={(today.by_status?.partial || 0) > 0 ? 'amber' : 'slate'}
            />
            <FunnelArrow />
            <FunnelNode
              label="→ Human"
              value={today.by_status?.human || 0}
              icon={ShieldAlert}
              tone={(today.by_status?.human || 0) > 0 ? 'indigo' : 'slate'}
            />
            <FunnelArrow />
            <FunnelNode
              label="Posted"
              value={today.notes_posted}
              icon={Send}
              tone={today.notes_posted > 0 ? 'emerald' : 'slate'}
              sub={
                typeof today.auto_posted === 'number' && today.auto_posted > 0
                  ? `${today.auto_posted} auto`
                  : null
              }
            />
          </div>
        </div>
      </section>

      {/* CATEGORY BREAKDOWN + TICKER */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="lg:col-span-1">
          <h2 className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">
            Today by category
          </h2>
          <div className="rounded-2xl border border-border bg-white p-4">
            {(today.by_category || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No dispatches yet today.</p>
            ) : (
              <div className="space-y-2">
                {today.by_category.map((c) => {
                  const maxN = today.by_category[0]?.n || 1;
                  const pct = Math.round((c.n / maxN) * 100);
                  return (
                    <div key={c.category} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono text-foreground">{c.category}</span>
                        <span className="font-semibold text-foreground">{c.n}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden">
                        <div
                          className="h-full bg-indigo-400 rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Recent decisions
            </h2>
            <span className="text-[0.6rem] text-muted-foreground">live · last 20</span>
          </div>
          <div className="rounded-2xl border border-border bg-white p-2 max-h-[460px] overflow-y-auto">
            {(recent || []).length === 0 ? (
              <p className="text-sm text-muted-foreground p-4">No recent decisions.</p>
            ) : (
              <div className="space-y-1">
                {recent.map((r) => (
                  <DecisionRow
                    key={r.run_id}
                    r={r}
                    isNew={newRunIds.has(r.run_id)}
                    onOpen={setOpenRunId}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      {openRunId && (
        <RunDetailModal runId={openRunId} onClose={() => setOpenRunId(null)} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// AUDIT VIEW - per-ticket flow diagram (historical)
// ---------------------------------------------------------------------------

const SOURCE_FILTERS = [
  { value: 'any', label: 'All' },
  { value: 'llm', label: 'LLM-picked' },
  { value: 'rule', label: 'Rule-based' },
  { value: 'none', label: 'Triage-skipped' },
];

const NODE_TONES = {
  active: {
    slate:   'bg-white border-slate-300 text-slate-900',
    indigo:  'bg-indigo-50 border-indigo-300 text-indigo-900',
    emerald: 'bg-emerald-50 border-emerald-300 text-emerald-900',
    amber:   'bg-amber-50 border-amber-300 text-amber-900',
    rose:    'bg-rose-50 border-rose-300 text-rose-900',
  },
  muted:   'bg-stone-50 border-dashed border-stone-300 text-stone-500',
  skipped: 'bg-stone-50/50 border-dashed border-stone-300 text-stone-400',
};

const FlowNode = ({ icon: Icon, label, primary, secondary, tone = 'slate', state = 'active', dots }) => {
  const cls = state === 'active' ? NODE_TONES.active[tone] : NODE_TONES[state];
  return (
    <div className={`relative flex-1 min-w-[140px] max-w-[220px] rounded-xl border px-3 py-2.5 ${cls}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="h-3.5 w-3.5 opacity-70" />
        <span className="text-[0.6rem] font-semibold uppercase tracking-[0.08em] opacity-70">
          {label}
        </span>
      </div>
      <div className="text-xs font-medium truncate">{primary || <span className="opacity-60">-</span>}</div>
      {secondary && (
        <div className="text-[0.65rem] mt-0.5 opacity-70 line-clamp-2">{secondary}</div>
      )}
      {dots && dots.length > 0 && (
        <div className="mt-1.5 flex gap-0.5">
          {dots.map((d, i) => (
            <span
              key={i}
              title={d.title}
              className={`h-1.5 w-1.5 rounded-full ${
                d.status === 'implemented' ? 'bg-emerald-500'
                : d.status === 'stub' ? 'bg-sky-400'
                : d.status === 'missing' ? 'bg-stone-300'
                : d.status === 'error' ? 'bg-rose-500'
                : 'bg-stone-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Connector = ({ dashed }) => (
  <div className="flex items-center justify-center shrink-0 w-6 text-stone-400">
    <div className="relative w-full flex items-center">
      <div className={`flex-1 border-t ${dashed ? 'border-dashed border-stone-300' : 'border-stone-300'}`} />
      <ChevronRight className="h-3 w-3 -ml-1.5 shrink-0" />
    </div>
  </div>
);

const argsPreview = (args) => {
  if (!args || typeof args !== 'object' || Object.keys(args).length === 0) return '';
  return Object.entries(args)
    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(', ');
};

const RunCard = ({ run, onOpen }) => {
  const [open, setOpen] = useState(false);
  const triage = run.triage || {};
  const triageRan = !!triage.ran;
  const triageEscalated = !!triage.escalated;
  const src = run.selection_source || '';
  const calls = run.endpoint_calls || [];
  const selections = run.agentic_selections || [];

  const triageState = triageRan ? 'active' : 'muted';
  const triageTone = triageEscalated ? 'indigo' : 'slate';
  const selectionSkipped = src === 'none';
  const selectionState = selectionSkipped ? 'skipped' : src ? 'active' : 'muted';
  const selectionTone = src === 'llm' ? 'emerald' : 'slate';
  const endpointState = selectionSkipped ? 'skipped' : calls.length ? 'active' : 'muted';
  const endpointTone = calls.some((c) => c.status === 'error') ? 'rose'
    : calls.some((c) => c.status === 'missing') ? 'amber' : 'slate';
  const draftTone =
    run.status === 'human' ? 'indigo'
    : run.status === 'ready' ? 'emerald'
    : run.status === 'partial' ? 'amber'
    : run.status === 'error' ? 'rose' : 'slate';

  return (
    <article className="bg-white rounded-2xl border border-border shadow-sm">
      <header className="flex items-start justify-between gap-3 px-5 py-3 border-b border-border/60">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground truncate">
            {run.subject || '(no subject)'}
          </h3>
          <p className="text-[0.7rem] text-muted-foreground">
            {run.customer_email || 'unknown'}
            {' · '}<span className="font-mono">{run.category || '-'}</span>
            {' · '}{new Date(run.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={run.status} />
          {run.draft_audience === 'agent' && (
            <span className="inline-flex items-center rounded-md bg-indigo-100 text-indigo-700 px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide">
              for agent
            </span>
          )}
          {typeof run.draft_confidence === 'number' && (
            <span className="inline-flex items-center rounded-md bg-stone-100 text-stone-700 px-2 py-0.5 text-[0.65rem] font-mono">
              {Math.round(run.draft_confidence * 100)}%
            </span>
          )}
          <a
            href={`https://lamptwist.gorgias.com/app/ticket/${run.gorgias_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-muted-foreground hover:bg-[hsl(var(--secondary))] hover:text-foreground"
          >
            <ExternalLink className="h-3 w-3" />
            Gorgias
          </a>
        </div>
      </header>

      <div className="px-5 py-4 overflow-x-auto">
        <div className="flex items-stretch gap-0 min-w-[720px]">
          <FlowNode icon={InboxIcon} label="Ticket" primary={`#${run.gorgias_id}`} secondary={run.language ? `lang: ${run.language}` : null} tone="slate" />
          <Connector />
          <FlowNode icon={Filter} label="Classify" primary={run.classifier?.category || run.category || '-'} secondary={typeof run.classifier?.confidence === 'number' ? `confidence ${(run.classifier.confidence * 100).toFixed(0)}%` : null} tone="slate" />
          <Connector />
          <FlowNode icon={ShieldAlert} label="Triage" state={triageState} tone={triageTone}
            primary={!triageRan ? 'skipped' : triageEscalated ? '→ human' : 'AI handles'}
            secondary={triageRan ? (triage.category ? `cat: ${triage.category}` : null) : 'classifier was confident'}
          />
          <Connector dashed={triageEscalated} />
          <FlowNode icon={Bot} label="Tool selection" state={selectionState} tone={selectionTone}
            primary={src === 'llm' ? 'LLM' : src === 'rule' ? 'rule-based' : src === 'none' ? 'skipped' : '-'}
            secondary={src === 'llm' ? `${selections.length} tool${selections.length === 1 ? '' : 's'}` : src === 'rule' ? 'by category' : null}
          />
          <Connector dashed={selectionSkipped} />
          <FlowNode icon={Settings2} label="Endpoints" state={endpointState} tone={endpointTone}
            primary={`${calls.length} call${calls.length === 1 ? '' : 's'}`}
            secondary={calls.length > 0 ? calls.slice(0, 2).map((c) => c.tool_name || c.endpoint).join(', ') + (calls.length > 2 ? '…' : '') : selectionSkipped ? 'none' : null}
            dots={calls.map((c) => ({ status: c.status, title: c.tool_name || c.endpoint }))}
          />
          <Connector />
          <FlowNode icon={FileText} label="Draft" tone={draftTone} primary={run.status}
            secondary={run.draft_audience === 'agent' ? 'for agent' : typeof run.draft_confidence === 'number' ? `conf ${Math.round(run.draft_confidence * 100)}%` : null}
          />
        </div>
      </div>

      <div className="px-5 pb-4 flex items-center gap-3">
        <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-[0.7rem] text-muted-foreground hover:text-foreground">
          <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
          {open ? 'Hide details' : 'Show details'}
        </button>
        {onOpen && (
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex items-center gap-1 text-[0.7rem] text-muted-foreground hover:text-foreground"
          >
            <ExternalLink className="h-3 w-3" />
            Open full trace
          </button>
        )}
        {open && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            {triageRan && triage.reason && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                <div className="text-[0.6rem] uppercase tracking-wider text-indigo-700 mb-1">Triage reason</div>
                <div className="text-indigo-900">{triage.reason}</div>
              </div>
            )}
            {run.classifier?.reason && (
              <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                <div className="text-[0.6rem] uppercase tracking-wider text-stone-600 mb-1">Classifier reason</div>
                <div className="text-stone-800">{run.classifier.reason}</div>
              </div>
            )}
            {calls.length > 0 && (
              <div className="md:col-span-2 rounded-lg border border-stone-200 bg-white p-3">
                <div className="text-[0.6rem] uppercase tracking-wider text-stone-600 mb-2">Tool calls</div>
                <div className="space-y-1.5">
                  {calls.map((c, i) => (
                    <div key={i} className="flex items-start gap-2 font-mono text-[0.7rem]">
                      <span className={`mt-0.5 h-1.5 w-1.5 rounded-full shrink-0 ${
                        c.status === 'implemented' ? 'bg-emerald-500'
                        : c.status === 'stub' ? 'bg-sky-400'
                        : c.status === 'missing' ? 'bg-stone-300'
                        : c.status === 'error' ? 'bg-rose-500' : 'bg-stone-300'
                      }`} />
                      <div className="min-w-0 flex-1">
                        <span className="text-foreground">{c.tool_name || c.endpoint}</span>
                        {argsPreview(c.args) && <span className="text-muted-foreground"> ( {argsPreview(c.args)} )</span>}
                        <span className="text-muted-foreground"> → {c.status}</span>
                        {c.selected_by && <span className="ml-1 text-[0.6rem] text-muted-foreground/70">[{c.selected_by}]</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {run.missing_reason && (
              <div className="md:col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="text-[0.6rem] uppercase tracking-wider text-amber-800 mb-1">Outcome</div>
                <div className="text-amber-900">{run.missing_reason}</div>
              </div>
            )}
            {run.auto_post && (run.auto_post.posted || run.auto_post.reason || run.auto_post.error) && (
              <div className={`md:col-span-2 rounded-lg border p-3 ${
                run.auto_post.posted
                  ? 'border-emerald-200 bg-emerald-50'
                  : run.auto_post.error
                    ? 'border-rose-200 bg-rose-50'
                    : 'border-stone-200 bg-stone-50'
              }`}>
                <div className={`text-[0.6rem] uppercase tracking-wider mb-1 ${
                  run.auto_post.posted ? 'text-emerald-700' : run.auto_post.error ? 'text-rose-700' : 'text-stone-600'
                }`}>
                  Auto-post
                </div>
                <div className={run.auto_post.posted ? 'text-emerald-900' : run.auto_post.error ? 'text-rose-900' : 'text-stone-800'}>
                  {run.auto_post.posted
                    ? `Pushed to Gorgias as internal note (confidence ${Math.round((run.draft_confidence || 0) * 100)}% ≥ ${Math.round((run.auto_post.threshold || 0) * 100)}%).`
                    : run.auto_post.error
                      ? `Gorgias post failed: ${run.auto_post.error}`
                      : `Held back - ${run.auto_post.reason}${typeof run.auto_post.threshold === 'number' ? ` (threshold ${Math.round(run.auto_post.threshold * 100)}%)` : ''}.`}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

const AuditView = ({ initialHeldBack = false }) => {
  const [filter, setFilter] = useState('any');
  const [escalatedOnly, setEscalatedOnly] = useState(false);
  const [heldBackOnly, setHeldBackOnly] = useState(initialHeldBack);
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openRunId, setOpenRunId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ source: filter });
      if (escalatedOnly) params.set('escalated', '1');
      if (heldBackOnly) params.set('held_back', '1');
      const { data } = await axiosInstance.get(`api/replai/pipeline/?${params.toString()}`);
      setData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, escalatedOnly, heldBackOnly]);

  // Client-side search across the already-loaded runs. Matches against
  // subject, customer email, category, Gorgias id, and triage reason.
  const filteredRuns = React.useMemo(() => {
    const runs = data?.runs || [];
    const q = query.trim().toLowerCase();
    if (!q) return runs;
    return runs.filter((r) => {
      const hay = [
        r.subject,
        r.customer_email,
        r.category,
        r.gorgias_id,
        r.triage?.reason,
        r.classifier?.reason,
        r.missing_reason,
        ...(r.endpoint_calls || []).map((c) => c.tool_name || c.endpoint),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [data, query]);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-[420px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search subject, email, category, reason…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-xl border border-border bg-white pl-9 pr-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={() => setHeldBackOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium transition ${
              heldBackOnly ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-border bg-white text-muted-foreground hover:text-foreground'
            }`}
            title="Show only runs that were dispatched but not auto-posted - low confidence, triage-escalated, or failed validation"
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Held back
          </button>
          <button
            type="button"
            onClick={() => setEscalatedOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium transition ${
              escalatedOnly ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-border bg-white text-muted-foreground hover:text-foreground'
            }`}
          >
            <ShieldAlert className="h-3.5 w-3.5" />
            Escalations
          </button>
          <div className="flex items-center gap-1 rounded-xl border border-border bg-white p-1">
            {SOURCE_FILTERS.map((f) => {
              const active = filter === f.value;
              return (
                <button key={f.value} type="button" onClick={() => setFilter(f.value)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                    active ? 'bg-[hsl(var(--accent))] text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {loading && !data && <p className="text-sm text-muted-foreground">Loading…</p>}
      {data && filteredRuns.length === 0 && (
        <div className="rounded-2xl border border-border bg-white p-10 text-center text-sm text-muted-foreground">
          <Activity className="h-6 w-6 mx-auto mb-2 text-muted-foreground/60" />
          {query ? `No runs match "${query}".` : 'No dispatch runs match this filter yet.'}
        </div>
      )}
      {data && filteredRuns.length > 0 && (
        <>
          {query && (
            <p className="text-xs text-muted-foreground">
              {filteredRuns.length} of {data.runs.length} runs match
            </p>
          )}
          <div className="space-y-4">
            {filteredRuns.map((r) => (
              <RunCard key={r.run_id} run={r} onOpen={() => setOpenRunId(r.run_id)} />
            ))}
          </div>
        </>
      )}
      {openRunId && (
        <RunDetailModal runId={openRunId} onClose={() => setOpenRunId(null)} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// root
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'live', label: 'Live' },
  { key: 'audit', label: 'Audit' },
];

const Pipeline = () => {
  const [tab, setTab] = useState('live');

  const header = (
    <div className="flex items-center gap-1 rounded-xl border border-border bg-white p-1">
      {TABS.map((t) => {
        const active = tab === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`rounded-lg px-4 py-1.5 text-xs font-medium transition ${
              active ? 'bg-[hsl(var(--accent))] text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <AppShell
      title="Pipeline"
      subtitle={
        tab === 'live'
          ? 'Operational dashboard - see how tickets are moving through the agent right now.'
          : 'Step-by-step audit of each ticket - classify → triage → tools → endpoints → draft.'
      }
      headerRight={header}
    >
      {tab === 'live' ? <LiveView /> : <AuditView />}
    </AppShell>
  );
};

export default Pipeline;
